import { useEffect, useRef, useState } from "react";

export function QrCameraReader({ onRead }: { onRead: (raw: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => stream?.getTracks().forEach((track) => track.stop());
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia)
          throw new Error(
            "Camera access is unavailable. Use manual entry or paste QR contents below."
          );
        const { default: jsQR } = await import("jsqr");
        if (cancelled) return;
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          stop();
          return;
        }
        const player = video.current;
        if (!player) {
          stop();
          return;
        }
        player.srcObject = stream;
        await player.play();
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context)
          throw new Error(
            "Camera frames could not be read. Please enter the details manually."
          );
        const scan = () => {
          if (cancelled) return;
          try {
            if (player.videoWidth && player.videoHeight) {
              const scale = Math.min(
                1,
                640 / Math.max(player.videoWidth, player.videoHeight)
              );
              canvas.width = Math.round(player.videoWidth * scale);
              canvas.height = Math.round(player.videoHeight * scale);
              context.drawImage(player, 0, 0, canvas.width, canvas.height);
              const pixels = context.getImageData(
                0,
                0,
                canvas.width,
                canvas.height
              );
              const result = jsQR(pixels.data, pixels.width, pixels.height);
              if (result) {
                stop();
                onRead(result.data);
                return;
              }
            }
            timer = setTimeout(scan, 250);
          } catch {
            stop();
            setError(
              "The QR could not be read. Close the scanner and try again, or enter the details manually."
            );
          }
        };
        scan();
      } catch (problem) {
        stop();
        if (!cancelled)
          setError(
            problem instanceof Error && problem.message.startsWith("Camera")
              ? problem.message
              : "Camera access failed. Allow camera access, or close this window and enter the details manually."
          );
      }
    };
    start();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      stop();
    };
  }, [onRead]);
  return (
    <div className="space-y-3">
      <video
        ref={video}
        autoPlay
        muted
        playsInline
        aria-label="QR scanner camera"
        className="w-full rounded-xl bg-f4f7ff aspect-square object-cover"
      />
      {error && (
        <p role="alert" className="text-sm text-fd380f">
          {error}
        </p>
      )}
    </div>
  );
}
